import React, { useContext, useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { QmsDataEditModalContext } from "../_common/QmsDataTable";
import cmtStageActions from "../../../_actions/_qmsActions/cmtStageActions";
import qmsTeamActions from "../../../_actions/_qmsActions/qmsTeamActions";

export default function QmsAdminModal_EditCmtStage(props) {
  const { divisionId } = props;

  const qmsDataEditModal = useContext(QmsDataEditModalContext);

  const dispatch = useDispatch();
  useEffect(() => dispatch(qmsTeamActions.getAll(divisionId)), []);
  const qmsTeams = useSelector((state) => state.qms.qmsTeams[divisionId] || null);
  const qmsTeamOptions = useMemo(
    () => (qmsTeams || []).map((qt) => ({ value: qt.qmsTeamId, label: qt.name })),
    [qmsTeams]
  );

  const [title, setTitle] = useState(
    qmsDataEditModal.modalState.data && "title" in qmsDataEditModal.modalState.data
      ? qmsDataEditModal.modalState.data.title
      : ""
  );
  const [description, setDescription] = useState(
    qmsDataEditModal.modalState.data && "description" in qmsDataEditModal.modalState.data
      ? qmsDataEditModal.modalState.data.description
      : ""
  );
  const [defaultOwnerTeam, setDefaultOwnerTeam] = useState(null);
  const [defaultDuration, setDefaultDuration] = useState(
    qmsDataEditModal.modalState.data && "duration" in qmsDataEditModal.modalState.data
      ? qmsDataEditModal.modalState.data.duration || 0
      : 0
  );
  const [formErrors, _setFormErrors] = useState({ title: null });
  const setFormError = (fieldName, message) =>
    _setFormErrors({ ...formErrors, [fieldName]: message });
  const clearFormError = (fieldName) =>
    _setFormErrors({ ...formErrors, [fieldName]: null });

  useEffect(() => {
    if (
      qmsDataEditModal.modalState.data &&
      "defaultOwnerTeamId" in qmsDataEditModal.modalState.data &&
      qmsDataEditModal.modalState.data.defaultOwnerTeamId &&
      qmsTeamOptions
    ) {
      const defaultOwnerTeam = (qmsTeamOptions || []).find(
        (qto) => qto.value === qmsDataEditModal.modalState.data.defaultOwnerTeamId
      );
      setDefaultOwnerTeam(defaultOwnerTeam || null);
    }
  }, [qmsTeamOptions]);

  const validate = (fieldName) => {
    switch (fieldName) {
      case "title":
        if (!title) setFormError(fieldName, "Required");
        else clearFormError(fieldName);
        break;
      default:
        break;
    }
    return;
  };

  const formIsInvalid = () => Object.values(formErrors).some((e) => e !== null);

  const handleClick_submit = async (e) => {
    e.preventDefault();
    const cmtStageData = {
      cmtStageId: qmsDataEditModal.modalState.data
        ? qmsDataEditModal.modalState.data.cmtStageId
        : 0,
      divisionId,
      title,
      description,
      defaultOwnerTeamId: defaultOwnerTeam ? defaultOwnerTeam.value : null,
      duration: defaultDuration || 0,
    };
    if (qmsDataEditModal.modalState.data)
      await dispatch(cmtStageActions.update(cmtStageData));
    else await dispatch(cmtStageActions.add(cmtStageData));
    qmsDataEditModal.toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={qmsDataEditModal.toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <Modal
      isOpen={qmsDataEditModal.modalState.showModal}
      toggle={qmsDataEditModal.toggleModal}
      fade={false}
      size={"md"}
    >
      <ModalHeader
        toggle={qmsDataEditModal.toggleModal}
        className="bg-info text-white"
        close={closeBtn}
      >
        <FontAwesomeIcon
          icon={qmsDataEditModal.modalState.data ? "edit" : "plus"}
          className="fa-fw mr-2"
        />
        {qmsDataEditModal.modalState.data ? `Edit ` : `Add New `}
        CMT Stage
      </ModalHeader>
      <ModalBody>
        <div className="form-row mb-2">
          <div className="form-group col-md-6 mb-0 pr-1">
            <label htmlFor="title" className="mb-0">
              <b>Stage Title:</b>*
            </label>
            <input
              type="text"
              name="title"
              id="title"
              className="form-control"
              maxLength="20"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={(e) => validate(e.target.name)}
            />
            {formErrors.title && (
              <span className="error-small">
                <small>{formErrors.title}</small>
              </span>
            )}
          </div>
        </div>
        <div className="form-row mb-2">
          <div className="form-group col-md-12 mb-0">
            <label htmlFor="description" className="mb-0">
              Stage Description:
            </label>
            <textarea
              name="description"
              id="description"
              className="form-control"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={(e) => validate(e.target.name)}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-body p-3">
            <h5 className="text-muted">Default Values</h5>
            <div className="alert alert-info p-1 mb-1">
              <FontAwesomeIcon icon="info-circle" className="fa-fw mr-1" />
              Below fields can be overridden during stage assignment.
            </div>
            <div className="form-row mb-2">
              <div className="form-group col-md-12 mb-0 pl-1">
                <div>
                  <label htmlFor="defaultOwnerTeam" className="mb-0">
                    Default Owner Team/User Group:
                  </label>
                </div>
                <Select
                  id="defaultOwnerTeam"
                  name="defaultOwnerTeam"
                  onChange={(selected) => setDefaultOwnerTeam(selected)}
                  value={defaultOwnerTeam}
                  options={qmsTeamOptions}
                  isClearable
                />
              </div>
            </div>
            <div className="form-row mb-0">
              <div className="form-group col-md-6 mb-0">
                <label htmlFor="defaultDuration" className="mb-0">
                  Default Duration:
                </label>
                <div className="input-group mb-3">
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0"
                    value={defaultDuration}
                    onChange={(e) => setDefaultDuration(e.target.value)}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">Days</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bg-lightgray">
        <div className="form-row form-group mb-0">
          <button className="btn btn-primary" name="add" onClick={handleClick_submit}>
            {qmsDataEditModal.modalState.data ? "Update" : "Add"}
          </button>
          <button
            className="btn btn-secondary ml-2"
            color="secondary"
            onClick={qmsDataEditModal.toggleModal}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
